<template>
	<efPageProgressBar />
	<div
		:class="[
			'app app-dashboard app-dashboard-' + effectiveTheme,
			{
				'app-dividers': appOption.appThemeDividers,
				'app-gradients': appOption.appThemeGradients,
				'app-shadows': appOption.appThemeShadows,
			},
		]"
		:data-bs-theme="effectiveTheme"
		:style="colors"
	>
		<router-view />
	</div>
	<toast-wrapper />
	<div :class="{ 'app-staging': isStaging }"></div>
</template>

<script setup lang="ts">
import { computed, watchEffect } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';

import efPageProgressBar from '@lib/progress/efPageProgressBar.vue';
import { type ProgressFinisher, usePageProgress } from '@lib/progress/usePageProgress';

import { useAppOptionsStore, hexToRgbList, readableOn } from '@lib/stores/app-options';
import { useColorScheme } from '@lib/composables/useColorScheme';
import ToastWrapper from '@lib/plugins/toasts/ToastWrapper.vue';

import '@lib/plugins/bootstrap';

/*
 * States
 */
const router = useRouter();

const appOption = useAppOptionsStore();
const { scheme } = useColorScheme();

const effectiveTheme = computed(() => (appOption.appTheme === 'auto' ? scheme.value : appOption.appTheme));

// Mirror the active theme onto <html> so Bootstrap's dark mode also reaches
// content rendered outside the .app root - e.g. modals and toasts that
// teleport to <body>. The .app element keeps its own :data-bs-theme binding,
// so nothing changes inside the app shell; this only stops teleported
// overlays from rendering light while the rest of the app is dark.
watchEffect(() => {
	document.documentElement.setAttribute('data-bs-theme', effectiveTheme.value);
});

/** The colours an admin picks, and the text that reads on the primary (readableOn()). */
const colors = computed<Record<string, string>>(() => ({
	'--bs-primary': appOption.$state.appPrimaryColor,
	'--bs-primary-rgb': hexToRgbList(appOption.$state.appPrimaryColor),
	'--ef-on-primary': readableOn(appOption.$state.appPrimaryColor),
	'--bs-secondary': appOption.$state.appSecondaryColor,
	'--bs-secondary-rgb': hexToRgbList(appOption.$state.appSecondaryColor),
}));

// Onto <html> as well, for the same overlays: on .app alone a dialog showed Bootstrap's own
// primary, built in at compile time
watchEffect(() => {
	for (const [name, value] of Object.entries(colors.value)) {
		document.documentElement.style.setProperty(name, value);
	}
});

const progresses = [] as ProgressFinisher[];
const isStaging = globalThis.env?.app?.env != 'production';

/*
 * Callbacks
 */

// Register interaction with the process bar
router.beforeEach(async () => {
	progresses.push(usePageProgress().start());
	document.body.scrollTop = 0;
	document.documentElement.scrollTop = 0;
});

router.afterEach(async () => {
	progresses.pop()?.finish();
});

axios.interceptors.request.use((config) => {
	progresses.push(usePageProgress().start());
	return config;
});

axios.interceptors.response.use(
	(resp) => {
		progresses.pop()?.finish();
		return resp;
	},
	(error) => {
		progresses.pop()?.finish();
		return Promise.reject(error);
	},
);

// Cleanup leftover backdrops if a RouterLink is clicked inside a model
router.afterEach(() => {
	document.body.classList.remove('modal-open');
	document.querySelectorAll('.modal-backdrop').forEach((b) => b.remove());
});
</script>

<style lang="scss">
.app-staging {
	--spacing: 2px;
	--color: var(--bs-red);
	--color-bg: var(--bs-light);

	// Fixed, not absolute: pinned to the window, so the frame and the badge stay in view on a
	// page longer than the screen instead of scrolling away with its first screenful.
	&:before {
		content: ' '; /* to ensure it displays */
		position: fixed;
		left: var(--spacing);
		right: var(--spacing);
		top: var(--spacing);
		bottom: var(--spacing);
		border: var(--spacing) dashed var(--color);
		border-radius: var(--bs-border-radius);
		pointer-events: none; /* user can't click on it */
		z-index: 1090;
	}

	&::after {
		content: 'Staging - Do not use in production'; /* to ensure it displays */
		position: fixed;
		color: var(--color);
		background-color: var(--color-bg);
		padding: 0.25rem 0.5rem;
		bottom: calc(3 * var(--spacing));
		pointer-events: none; /* user can't click on it */
		left: 50%;
		transform: translate(-50%, 0);
		z-index: 1090;
		white-space: nowrap !important;
		text-transform: uppercase;
		font-family: 'Courier';
		font-weight: bolder;
		border: 5px solid var(--color);
		border-radius: 0.25rem;
	}
}

// A frame on the screen, not on paper: printed, it lands on every document the app prints.
@media print {
	.app-staging {
		display: none;
	}
}

// Add improvement for bitwarden var
#bit-notification-bar-spacer {
	position: fixed;
}
</style>
